import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap, switchMap, map } from 'rxjs/operators';
import { FrameContext, Entity, BindingData, BindingPathConverter } from '@farris/devkit';
import { BefRepository, BefDataPathUtil, ResponseInfo } from '@farris/bef';
import { FormLoadingService } from '../form-loading/form-loading.service';
import { AttachmentInfo, ServerAttachmentInfo } from './types';
import { AttachmentUtil } from './attachment.util';

/**
 * éä»¶è°ç¨
 */
@Injectable()
class AttachmentDataService {

  /**
   * å®ä½ä»åº
   */
  private get repository(): BefRepository<Entity> {
    return this.frameContext.repository as BefRepository<Entity>;
  }

  /**
   * ç»å®æ°æ®
   */
  private get bindingData(): BindingData {
    return this.frameContext.bindingData;
  }

  constructor(private frameContext: FrameContext, private loadingService: FormLoadingService) {
  }

  /**
   * æ´æ°éä»¶ä¿¡æ¯
   */
  public updateRow(attachmentInfoFieldPath: string, attachmentInfo: AttachmentInfo): Observable<any> {
    const restService = this.repository.restService;
    const baseUri = restService.baseUri;
    const updateUri = `${baseUri}/service/updateattachment`;
    const serverAttachInfo = this.createUpdateAttachInfo(attachmentInfoFieldPath, attachmentInfo);
    const body = {
      updateAttachInfo: serverAttachInfo,
      requestInfo: restService.buildRequestInfo()
    };
    const options = {
      body: body
    };
    this.loadingService.show();
    return restService.invoke(updateUri, 'PUT', null, options).pipe(
      switchMap((responseInfo: ResponseInfo) => {
        return this.syncAttachmentInfosToClient();
      }),
      tap(() => {
        this.loadingService.hide();
      })
    );
  }
  /**
   * éè¿å±æ§åæ´æ°éä»¶ä¿¡æ¯
   * @param attachmentInfoFieldPath éä»¶å­æ®µ
   * @param attachmentInfo éä»¶ä¿¡æ¯
   */
  public updateRowWithPropertyName(attachmentInfoFieldPath: string, attachmentInfo: AttachmentInfo): Observable<any> {
    const restService = this.repository.restService;
    const baseUri = restService.baseUri;
    const updateUri = `${baseUri}/service/updateattachmentwithproptyname`;
    const serverAttachInfo = this.createUpdateAttachInfo(attachmentInfoFieldPath, attachmentInfo);
    const propertyName = attachmentInfoFieldPath.split('/').filter(p => p).pop();
    const body = {
      updateAttachInfo: serverAttachInfo,
      propertyName,
      requestInfo: restService.buildRequestInfo()
    };
    const options = {
      body: body
    };
    this.loadingService.show();
    return restService.invoke(updateUri, 'PUT', null, options).pipe(
      switchMap((responseInfo: ResponseInfo) => {
        return this.syncAttachmentInfosToClient();
      }),
      tap(() => {
        this.loadingService.hide();
      })
    );
  }
  /**
   * å é¤éä»¶
   * @param attachmentInfoFieldPath 
   * @param attachmentInfo 
   * @returns 
   */
  public removeAttachment(attachmentInfoFieldPath: string, attachmentInfo: AttachmentInfo) {
    const restService = this.repository.restService;
    const baseUri = restService.baseUri;
    const updateUri = `${baseUri}/service/deleteattachment`;
    const serverAttachInfo = this.createUpdateAttachInfo(attachmentInfoFieldPath, attachmentInfo);
    const propertyName = attachmentInfoFieldPath.split('/').filter(p => p).pop();
    const body = {
      deleteAttachInfo: serverAttachInfo,
      requestInfo: restService.buildRequestInfo(),
      propertyName,
    };
    const options = {
      body: body
    };
    this.loadingService.show();
    return restService.invoke(updateUri, 'PUT', null, options).pipe(
      switchMap((responseInfo: ResponseInfo) => {
        return this.syncAttachmentInfosToClient();
      }),
      tap(() => {
        this.loadingService.hide();
      })
    );
  }
  /**
   * æ¹éåå»ºéä»¶è¡æ°æ®
   */
  public updateRows(attachmentInfoFieldPath: string, attachmentInfos: AttachmentInfo[]): Observable<any> {
    const restService = this.repository.restService;
    const baseUri = restService.baseUri;
    const updateUri = `${baseUri}/service/batchuploadattachment`;
    const serverAttachInfo = this.createBatchCreateAttachInfo(attachmentInfoFieldPath, attachmentInfos);
    const isRootEntity = serverAttachInfo.NodeCodes.length === 0;

    const body = {
      batchUploadInfo: serverAttachInfo,
      requestInfo: restService.buildRequestInfo()
    };
    const options = {
      body: body
    };
    return restService.invoke(updateUri, 'PUT', null, options).pipe(
      switchMap((responseInfo: ResponseInfo) => {
        return this.appendAttachmentInfosToClient(responseInfo.returnValue, isRootEntity);
      }),
      tap(() => {
        this.loadingService.hide();
      })
    );
  }
  /**
   * æ¹éåå»ºéä»¶è¡æ°æ®
   */
  public updateRowsWithConfigs(attachmentInfoFieldPath: string, attachmentInfos: AttachmentInfo[], configs: { [prop: string]: any }): Observable<any> {
    const restService = this.repository.restService;
    const baseUri = restService.baseUri;
    const updateUri = `${baseUri}/service/batchuploadattachment`;
    const serverAttachInfo = this.createBatchCreateAttachInfo(attachmentInfoFieldPath, attachmentInfos);
    // const isRootEntity = serverAttachInfo.NodeCodes.length === 0;

    const body = {
      batchUploadInfo: serverAttachInfo,
      requestInfo: restService.buildRequestInfo()
    };
    const options = {
      body: body
    };
    return restService.invoke(updateUri, 'PUT', null, options).pipe(
      switchMap((responseInfo: ResponseInfo) => {
        return this.appendAttachmentInfos(responseInfo.returnValue, configs);
      }),
      tap(() => {
        this.loadingService.hide();
      })
    );
  }
  /**
   * æ ¹æ®å±æ§åæ¹éåå»ºéä»¶è¡æ°æ®
   */
  public updateRowsWithPropertyName(attachmentInfoFieldPath: string, attachmentInfos: AttachmentInfo[]): Observable<any> {
    const restService = this.repository.restService;
    const baseUri = restService.baseUri;
    const updateUri = `${baseUri}/service/batchuploadattachmentwithproptyname`;
    const serverAttachInfo = this.createBatchCreateAttachInfo(attachmentInfoFieldPath, attachmentInfos);
    const isRootEntity = serverAttachInfo.NodeCodes.length === 0;
    const propertyName = attachmentInfoFieldPath.split('/').filter(p => p).pop();

    const body = {
      batchUploadInfo: serverAttachInfo,
      propertyName,
      requestInfo: restService.buildRequestInfo()
    };
    const options = {
      body: body
    };
    return restService.invoke(updateUri, 'PUT', null, options).pipe(
      switchMap((responseInfo: ResponseInfo) => {
        return this.appendAttachmentInfosToClient(responseInfo.returnValue, isRootEntity);
      }),
      tap(() => {
        this.loadingService.hide();
      })
    );
  }

  /**
   * åå»ºæå¡å¨ç«¯éè¦çæ´æ°ä¿¡æ¯
   */
  private createUpdateAttachInfo(attachmentInfoFieldPath: string, attachmentInfo: AttachmentInfo): ServerAttachmentInfo {

    const attachmentId = attachmentInfo.attachmentId;
    const parentBindingPathArray = BindingPathConverter.toBindingPathArray(attachmentInfoFieldPath);
    parentBindingPathArray.pop();
    const nodeCodes = BefDataPathUtil.convertToObjectCodes(parentBindingPathArray, this.bindingData);
    const hiretryIds = BefDataPathUtil.convertToDataIdsForUpdate(parentBindingPathArray, this.bindingData);

    const serverAttachInfo: ServerAttachmentInfo = {
      NodeCodes: nodeCodes,
      HiretryIds: hiretryIds,
      AttachmentIds: [attachmentId],
      AttachmentId: attachmentId
    };

    return serverAttachInfo;
  }

  /**
   * åå»ºæå¡å¨ç«¯éè¦çæ¹éæ°å¢éä»¶ä¿¡æ¯
   */
  private createBatchCreateAttachInfo(attachmentInfoFieldPath: string, attachmentInfo: AttachmentInfo[]): ServerAttachmentInfo {
    const attachmentIds = AttachmentUtil.peekAttachmentIds(attachmentInfo);

    const parentBindingPathArray = BindingPathConverter.toBindingPathArray(attachmentInfoFieldPath);
    parentBindingPathArray.pop();
    const nodeCodes = BefDataPathUtil.convertToObjectCodes(parentBindingPathArray, this.bindingData);
    const hiretryIds = BefDataPathUtil.convertToDataIdsForAdd(parentBindingPathArray, this.bindingData);

    const serverAttachInfo = {
      NodeCodes: nodeCodes,
      HiretryIds: hiretryIds,
      AttachmentIds: attachmentIds,
      AttachmentId: null
    };

    return serverAttachInfo;
  }

  /**
   * åæ­¥æå¡å¨ç«¯ææ°ä¿¡æ¯å°å®¢æ·ç«¯
   * @todo:
   * 1ãä¸»å¯¹è±¡æ¹éæ°å¢æ¶ä¸æ¯æ
   */
  public syncAttachmentInfosToClient() {
    const rootDataId = this.bindingData.list.currentId;
    return this.repository.updateEntityById(rootDataId);
  }

  /**
   * è¿½å ä¸»è¡¨æ°æ®å°å®¢æ·ç«¯
   */
  public appendAttachmentInfosToClient(listData: any[], isRootEntity: boolean): Observable<any[]> {
    if (isRootEntity === true) {
      const entities = this.repository.buildEntities(listData);
      this.repository.entityCollection.addEntities(entities);
      return of(listData);
    } else {
      const rootDataId = this.bindingData.list.currentId;
      return this.repository.updateEntityById(rootDataId).pipe(
        map(() => listData)
      );
    }
  }
  public appendAttachmentInfos(listData: any[], keyValues: { [prop: string]: any }): Observable<any[]> {
    const entities = this.repository.buildEntities(listData);
    this.repository.entityCollection.addEntities(entities);
    // æ´æ°å®ä½ä½¿ä¹äº§çåæ´é
    this.updateEntities(entities, keyValues);
    return of(listData);
  }
  private updateEntities(entities: Entity[], keyValues: { [prop: string]: any }) {
    entities.forEach((entity: Entity) => {
      this.updateEntity(entity, keyValues);
    });
  }
  private updateEntity(target: Entity, keyValues: { [prop: string]: any }) {
    Object.keys(keyValues).forEach((key: string) => {
      this.setValueByPath(target, key, keyValues[key]);
    });
  }
  private setValueByPath(target: object, path: string, value: any) {
    if (target) {
      const paths = path.split('.');
      if (paths.length <= 1) {
        target[path] = value;
      } else {
        paths.slice(0, -1).reduce((prev, path) => {
          if (!(prev.hasOwnProperty(path) || prev['__proto__'].hasOwnProperty(path))) {
            prev[path] = {};
          }
          return prev[path];
        }, target)[paths[paths.length - 1]] = value;
      }
    }
  }
}


export { AttachmentDataService };
